import { motion } from "framer-motion";
import { TrendingUp } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";

const GROWTH = [
  { year: "2022", value: 65, color: "bg-teal" },
  { year: "2023", value: 100, color: "bg-teal" },
  { year: "2024", value: 150, color: "bg-teal-light" },
  { year: "2025", value: 305, color: "bg-ember", badge: "record" },
  { year: "2026", value: 500, target: true },
];

const MAX = 500;

export const GrowthChart = () => (
  <section className="py-16" data-testid="growth-section">
    <div className="mx-auto max-w-6xl px-6">
      <Reveal className="text-center">
        <span className="inline-flex items-center gap-2 rounded-full bg-ember-subtle px-4 py-1.5 font-mono-brand text-[11px] font-bold uppercase tracking-[0.25em] text-ember">
          <TrendingUp size={14} />
          The growth story
        </span>
        <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-coal sm:text-5xl">
          From 65 to 305 learners, <span className="text-teal">and climbing.</span>
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-coal-muted">
          Learners reached each year since our first night at the Labia Theatre. The dashed bar is
          where your sponsorship takes us next.
        </p>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-14 rounded-[2.5rem] border border-coal/5 bg-white p-6 shadow-[0_24px_70px_-35px_rgba(13,124,133,0.5)] sm:p-10">
          <div className="flex h-72 items-end justify-between gap-3 border-b-2 border-coal/10 sm:gap-8">
            {GROWTH.map((g, i) => (
              <div key={g.year} className="flex h-full flex-1 flex-col items-center justify-end">
                <motion.span
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.5 + i * 0.15, duration: 0.5 }}
                  className={`mb-2 font-display text-lg font-extrabold sm:text-2xl ${
                    g.target ? "text-gold" : g.value === 305 ? "text-ember" : "text-teal"
                  }`}
                  data-testid={`growth-value-${g.year}`}
                >
                  {g.value}
                  {g.target && <span className="block text-center font-mono-brand text-[10px] font-bold uppercase tracking-[0.2em]">target</span>}
                  {g.badge && <span className="block text-center font-mono-brand text-[10px] font-bold uppercase tracking-[0.2em]">{g.badge}</span>}
                </motion.span>
                <motion.div
                  initial={{ height: 0 }}
                  whileInView={{ height: `${(g.value / MAX) * 100}%` }}
                  viewport={{ once: true, margin: "-60px" }}
                  transition={{ duration: 1.1, delay: i * 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className={`w-full max-w-24 rounded-t-2xl ${
                    g.target ? "border-4 border-dashed border-gold bg-gold/10" : g.color
                  }`}
                  data-testid={`growth-bar-${g.year}`}
                />
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-between gap-3 sm:gap-8">
            {GROWTH.map((g) => (
              <span
                key={g.year}
                className="flex-1 text-center font-mono-brand text-xs font-bold tracking-widest text-coal-muted"
              >
                {g.year}
              </span>
            ))}
          </div>
          <p className="mt-8 text-center text-xs leading-relaxed text-coal-muted">
            2025 includes the 105-learner study-skills workshop that set our single-term record. The
            2026 target funds two annual residential camps, 100 learners each, on the road to 500.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);
